// @flow
import React, { Component } from 'react';
import styled, { withTheme } from 'styled-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import Radio from 'common/domain/radio';
import { openSnackbar } from 'common/modules/ui/actions/uiActions';
import Card from 'common/components/Card';
import Page from 'statsradio/components/page/Page';
import ConfirmationDialog from 'common/components/ui/ConfirmationDialog';
import { Footer } from 'statsradio/themes/v2/Stylesheet';

import EditCustomerDialog from './components/EditCustomerDialog';
import CustomerListAdmin from './components/CustomerListAdmin';
import RadioCustomer from './types/RadioCustomer';
import { getRadio, getToken } from '../authentication/session/reducers/sessionReducer';
import CustomersAPI from './services/CustomersAPI';
import CustomerPagination from '../pagination/components/Pagination';
import Paging from '../pagination/domain/paging';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: ${({ theme }) => theme.metrics.largeMargin}px;
`;

const StyledFooter = styled(Footer)`
  margin-top: ${({ theme: { V2 } }) => V2.metrics.margin.normal}px;
`;

const ErrorWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const Row = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.metrics.largeMargin}px;
`;

type Props = {
  invitation: {
    error: boolean,
  },
  radio: Radio,
  token: string,

  t: any,
  actions: {
    openSnackbar: (type: string, message: string) => mixed,
    fetchCustomers: () => mixed,
  },
  theme: any,
};

type State = {
  error: boolean,
  fetching: boolean,
  customers: RadioCustomer[],
  showAddCustomerDialog: boolean,
  editCustomer: {
    showDialog: boolean,
    customer: ?RadioCustomer,
  },
  deleteCustomer: {
    showDialog: boolean,
    customer: ?RadioCustomer,
  },
  page: number,
  paging: Paging,
};

class CustomersPageAdmin extends Component<Props, State> {
  constructor() {
    super();

    this.state = {
      error: false,
      fetching: false,
      customers: [],
      showAddCustomerDialog: false,
      editCustomer: {
        showDialog: false,
        customer: undefined,
      },
      deleteCustomer: {
        showDialog: false,
        customer: undefined,
      },
      page: 1,
      paging: new Paging({ currentPage: 1, numberOfPages: 1 }),
    };
  }

  fetchCustomers = async () => {
    this.setState({ fetching: true, error: false });
    const { token } = this.props;
    const { page } = this.state;
    try {
      const results = await CustomersAPI.fetchAllCustomers(token, page);
      const paging = results.getPaging();

      this.setState({ fetching: false, customers: results.items, paging: paging });
    } catch (error) {
      this.setState({ fetching: false, error: true });
    }
  };

  deleteCustomer = (customer: RadioCustomer) => {
    this.setState({ deleteCustomer: { customer, showDialog: true } });
  };

  closeDeleteDialog = () => {
    this.setState({ deleteCustomer: { showDialog: false, customer: undefined } });
  };

  onDeleteCustomerConfirmed = async () => {
    const { t, token, radio: { callSign } } = this.props;
    const { deleteCustomer: { customer } } = this.state;

    try {
      if (customer) {
        await CustomersAPI.deleteCustomer(callSign, token, customer);
        const customers = this.state.customers.filter(c => !c.isSameAs(customer));
        this.setState({ customers });
        this.props.actions.openSnackbar('message', t('deletedCustomerSuccess'));
      }
    } catch (error) {
      this.props.actions.openSnackbar('error', t('couldNotDeleteCustomer'));
    }

    this.closeDeleteDialog();
  };

  onCurrentPageChange = (page: number) => {
    this.setState({ page: page }, () => {
      this.fetchCustomers();
    });
  };

  componentDidMount() {
    this.fetchCustomers();
  }

  componentDidUpdate(nextProps: Props) {
    if (this.props.radio !== nextProps.radio) {
      this.fetchCustomers();
    }
  }

  onEditCustomerDialogOpen = (customer: RadioCustomer): void => {
    this.setState({
      editCustomer: { showDialog: true, customer: customer },
    });
  };

  onEditCustomerDialogClose = (): void => {
    this.setState({
      editCustomer: { showDialog: false, customer: undefined },
    });
  };

  onEditSuccess = (radioCustomers: RadioCustomer[]): void => {
    this.setState({ customers: radioCustomers, editCustomer: { showDialog: false, customer: undefined } });
  };

  renderError(error: string) {
    const { theme } = this.props;

    return (
      <Card style={{ marginTop: theme.metrics.largeMargin }}>
        <ErrorWrapper style={theme.fonts.style.main}>{error}</ErrorWrapper>
      </Card>
    );
  }

  render() {
    const { t } = this.props;
    const { error, fetching, paging } = this.state;
    const displayPagination = paging.isRequired();

    return (
      <Page title={t('customersPageTitleAdmin')} useThemeV2>
        <Wrapper>
          <Row>
            <div className="col-lg-10 col-lg-offset-1 col-xs-12">
              {!error &&
              <CustomerListAdmin
                customers={this.state.customers}
                onEditOpen={this.onEditCustomerDialogOpen}
                onDeleteCustomer={this.deleteCustomer}
                loading={fetching}
              />
              }
              {error && this.renderError(t('couldNotLoadCustomers'))}
            </div>
          </Row>
        </Wrapper>
        <StyledFooter>
          {displayPagination &&
          <CustomerPagination paging={paging} onCurrentPageChange={this.onCurrentPageChange} />
          }
        </StyledFooter>
        <ConfirmationDialog
          open={this.state.deleteCustomer.showDialog}
          onCancel={this.closeDeleteDialog}
          onConfirm={this.onDeleteCustomerConfirmed}
          action={t('deleteCustomer')}
        />
        {this.state.editCustomer.customer &&
        <EditCustomerDialog
          onEditSuccess={this.onEditSuccess}
          onClose={this.onEditCustomerDialogClose}
          visible={this.state.editCustomer.showDialog}
          customer={this.state.editCustomer.customer}
        />}
      </Page>
    );
  }
}

function mapStateToProps(state): Object {
  return {
    invitation: {
      error: state.customers.customerInvitation.error,
    },
    radio: getRadio(state),
    token: getToken(state),
  };
}

function mapDispatchToProps(dispatch: *): Object {
  return {
    actions: bindActionCreators({ openSnackbar }, dispatch),
  };
}

export default withTheme(translate('customers')(connect(mapStateToProps, mapDispatchToProps)(CustomersPageAdmin)));



// WEBPACK FOOTER //
// ./src/statsradio/modules/customers/CustomersPageAdmin.js